import React from 'react';
import { Timer, AlertTriangle, Receipt, ListChecks, ChevronRight } from 'lucide-react';
import type { MetricasFaturamento } from '../../../../src/lib/financeiro/metricasFaturamento';

interface Props {
  metricas: MetricasFaturamento;
  rotuloPeriodo: string;
  /** Abre a lista das faturas por tras do numero. */
  onVerForaDoPrazo: () => void;
}

/** Mesmo formato do Painel Executivo: um decimal e a palavra "dias". */
export const fmtDias = (v: number | null) =>
  v === null ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} dias`;

export const fmtPct = (v: number | null) =>
  v === null ? '—' : `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

function KpiCard({
  icon: Icon, tint, titulo, valor, subtexto, badge, badgeTint, onClick, acaoRotulo,
}: {
  icon: React.ElementType; tint: string; titulo: string; valor: string;
  subtexto?: string; badge?: string; badgeTint?: string;
  onClick?: () => void; acaoRotulo?: string;
}) {
  const conteudo = (
    <>
      <div className={`p-2.5 rounded-xl shrink-0 ${tint}`}><Icon size={18} /></div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate">{titulo}</p>
        <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5 truncate">{valor}</p>
        {subtexto && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{subtexto}</p>}
        {badge && <p className={`text-[11px] font-semibold mt-0.5 ${badgeTint || 'text-gray-500 dark:text-gray-400'}`}>{badge}</p>}
        {acaoRotulo && (
          <p className="text-[11px] font-semibold text-primary mt-1 inline-flex items-center gap-1">
            {acaoRotulo} <ChevronRight size={12} />
          </p>
        )}
      </div>
    </>
  );

  const classeBase = 'bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex items-start gap-3';

  // Vira botao so quando ha para onde ir: card clicavel sem destino confunde.
  if (!onClick) return <div className={classeBase}>{conteudo}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${classeBase} w-full text-left hover:ring-2 hover:ring-primary/40 transition-shadow`}
    >
      {conteudo}
    </button>
  );
}

const KpiCardsFaturamento: React.FC<Props> = ({ metricas, rotuloPeriodo, onVerForaDoPrazo }) => {
  const d = metricas.departamento;

  // A cor segue a TAXA, não o número absoluto: 3 atrasos em 200 boletos é outra
  // conversa que 3 em 5, e pintar o card pelo valor bruto puniria volume.
  const nivel = d.taxaForaDoPrazo === null ? 'vazio'
    : d.taxaForaDoPrazo > 0.2 ? 'alto'
      : d.taxaForaDoPrazo >= 0.1 ? 'atencao' : 'controle';

  const tintAtraso =
    nivel === 'alto' ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
      : nivel === 'atencao' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400'
        : 'bg-primary/10 text-primary';

  const badgeTintAtraso =
    nivel === 'alto' ? 'text-red-600 dark:text-red-400'
      : nivel === 'atencao' ? 'text-amber-600 dark:text-amber-400'
        : 'text-primary';

  const motivoTop = metricas.rankingMotivos[0];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <KpiCard
        icon={Timer} tint="bg-primary/10 text-primary"
        titulo="Tempo médio de faturamento"
        valor={fmtDias(d.tempoMedioDias)}
        subtexto={
          d.comTempoMedido === d.totalEmitidos
            ? 'Da fatura ficar disponível até o boleto sair'
            : `Da disponibilidade até a emissão · ${d.comTempoMedido} de ${d.totalEmitidos} com data de origem`
        }
      />
      <KpiCard
        icon={AlertTriangle} tint={tintAtraso}
        titulo="Fora do prazo padrão"
        // O denominador fica no valor, não numa nota de rodapé: "3" sozinho não
        // diz nada, e comparar pessoas pelo absoluto pune quem tem carteira maior.
        valor={`${d.foraDoPrazo} de ${d.totalEmitidos}`}
        subtexto={`Emitidos até 5 dias úteis antes do vencimento · ${rotuloPeriodo}`}
        badge={d.taxaForaDoPrazo !== null ? `${fmtPct(d.taxaForaDoPrazo)} das emissões` : undefined}
        badgeTint={badgeTintAtraso}
        onClick={d.foraDoPrazo > 0 ? onVerForaDoPrazo : undefined}
        acaoRotulo={d.foraDoPrazo > 0 ? 'Ver faturas' : undefined}
      />
      <KpiCard
        icon={Receipt} tint="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400"
        titulo="Boletos faturados"
        valor={d.totalEmitidos.toLocaleString('pt-BR')}
        subtexto={
          metricas.reemissoesIgnoradas || metricas.avulsosIgnorados
            ? `Fora da conta: ${metricas.reemissoesIgnoradas} reemissões e ${metricas.avulsosIgnorados} avulsos`
            : 'Primeiras emissões, sem avulsos nem reemissões'
        }
      />
      <KpiCard
        icon={ListChecks} tint="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
        titulo="Motivo mais frequente"
        valor={motivoTop ? motivoTop.motivo : '—'}
        subtexto={
          motivoTop
            ? `${motivoTop.qtde} de ${d.foraDoPrazo - metricas.foraDoPrazoSemMotivo} atrasos justificados`
            : 'Nenhum motivo registrado no período'
        }
      />
    </div>
  );
};

export default KpiCardsFaturamento;
